import { Box } from '@mui/material'
import styled from '@emotion/styled/macro'
import theme from 'theme'

/* Every select on the data steps: 48px, the value in the CTA colour. The colour
 * goes on the value element — the shared select colours its own root, which a
 * root-level rule here only ties with — and the doubled parent outranks MUI's
 * disabled ramp, so an applied row stays read-only but legible. */
export const fieldSelectSx = (filled: boolean) => {
  const color = filled ? theme.colors.cta : theme.colors.menuItemText

  return {
    height: '48px',
    fontSize: theme.typography.fontSize.large,
    fontWeight: theme.typography.fontWeight.bold,
    letterSpacing: '0.1px',
    '&& .MuiSelect-select': { color, WebkitTextFillColor: color },
    /* An open field reads as CTA whatever it holds, as the shared select does. */
    '&&.Mui-focused .MuiSelect-select': {
      color: theme.colors.cta,
      WebkitTextFillColor: theme.colors.cta
    },
    '&.Mui-disabled svg': { color: theme.colors.devicesSecondaryText }
  }
}

export const FilterRowsWrapper = styled(Box)`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md}px;

  /* Three equal fields, then the row's remove control. */
  .filter-row {
    display: flex;
    align-items: center;
    gap: ${theme.spacing.xSmall}px;
    width: 100%;
  }

  .filter-row-field {
    flex: 1;
    min-width: 0;
  }

  .delete-button {
    margin-left: ${theme.spacing.xSmall}px;
    padding: 0;

    svg {
      color: ${theme.colors.dropdownLabel};
    }

    &:hover svg {
      color: ${theme.colors.cta};
    }

    &.Mui-disabled svg {
      color: ${theme.colors.btnSecondaryDisabledText};
    }
  }
`

/* Add Filter on the left, Preview Data as a text-only action on the right. */
export const FilterActionsRow = styled(Box)`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .add-filter-button {
    background: ${theme.colors.btnTertiaryBg};
    border: none;
    color: ${theme.colors.cta};
    font-size: ${theme.typography.fontSize.large};
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 20px;

    &:hover {
      background: ${theme.colors.btnTertiaryBg};
      border: none;
      color: ${theme.colors.cta};
    }
  }
`

export const PreviewPaneWrapper = styled(Box)`
  width: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${theme.spacing.md}px;

  /* Row-count line above the table, as on the design's preview panel. */
  .preview-row-count {
    color: ${theme.colors.dropdownLabel};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-weight: ${theme.typography.fontWeight.normal};
    font-style: normal;
    line-height: 22px;

    span {
      color: ${theme.colors.menuItemText};
    }
  }

  /* The spinner shown while the first preview loads. */
  .preview-placeholder {
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    width: 100%;
  }

  .section-hint {
    color: ${theme.colors.devicesSecondaryText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-weight: ${theme.typography.fontWeight.normal};
    font-style: normal;
    line-height: 20px;
    letter-spacing: 0.25px;
  }
`

/* Shown in place of the mapping columns until the filters step is applied. */
export const MappingEmptyState = styled(Box)`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.md}px;
  padding: ${theme.spacing.medium}px;
  border-radius: ${theme.attributes.borderRadius.tertiary};
  background: ${theme.colors.surfaceContainerBackground};

  .empty-state-title {
    width: 100%;
    text-align: center;
    color: ${theme.colors.menuItemText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.xLarge};
    font-weight: ${theme.typography.fontWeight.bold};
    font-style: normal;
    line-height: 20px;
  }

  .empty-state-description {
    width: 100%;
    text-align: center;
    color: ${theme.colors.devicesSecondaryText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-weight: ${theme.typography.fontWeight.normal};
    font-style: normal;
    line-height: 20px;
  }
`

/* Two equal columns, Onyxia's target fields against the Jira values they map to. */
export const MappingListWrapper = styled(Box)`
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: ${theme.spacing.md}px;

  .section {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.md}px;
  }

  .header {
    width: fit-content;
    white-space: nowrap;
    color: ${theme.colors.devicesSecondaryText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-weight: ${theme.typography.fontWeight.normal};
    font-style: normal;
    line-height: 20px;
    letter-spacing: 0.25px;
  }

  .list {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.md}px;
  }

  .list-item-onyxia {
    display: flex;
    flex-direction: column;
    justify-content: center;
    overflow: hidden;
    height: 48px;
    padding: 13px ${theme.spacing.small}px;
    border-radius: ${theme.attributes.borderRadius.tertiary};
    background: ${theme.colors.surfaceContainerBackground};
  }

  .list-item-text {
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${theme.colors.menuItemText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-weight: ${theme.typography.fontWeight.bold};
    font-style: normal;
    line-height: 22px;
  }
`
